import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { PricingSummary } from './PricingSummary';
import { SmsConsent } from '../common/SmsConsent';

const inputClasses =
  'mt-1 bg-[#050814] border-[#B8860B]/30 text-[#F4F1EA] placeholder:text-[#F4F1EA]/40 focus-visible:ring-[#B8860B]';

export const RentalBookingForm = ({
  equipment,
  attachments,
  formData,
  pricing,
  bookingLoading,
  smsConsent,
  onSmsConsentChange,
  onInputChange,
  onSubmit,
}) => {
  const showAttachments = equipment.category === 'Tractors' && attachments.length > 0;
  return (
    <Card className="shadow-xl bg-[#0B132B] border border-[#B8860B]/30">
      <CardContent className="p-8">
        <h2 className="text-2xl font-bold text-[#F4F1EA] mb-6">Book This Equipment</h2>

        <form onSubmit={onSubmit} className="space-y-6" data-testid="rental-booking-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date" className="text-[#F4F1EA]">Start Date</Label>
              <Input
                data-testid="rental-start-date"
                type="date"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={onInputChange}
                min={new Date().toISOString().split('T')[0]}
                required
                className={`${inputClasses} [color-scheme:dark]`}
              />
            </div>
            <div>
              <Label htmlFor="end_date" className="text-[#F4F1EA]">End Date</Label>
              <Input
                data-testid="rental-end-date"
                type="date"
                id="end_date"
                name="end_date"
                value={formData.end_date}
                onChange={onInputChange}
                min={formData.start_date || new Date().toISOString().split('T')[0]}
                required
                className={`${inputClasses} [color-scheme:dark]`}
              />
            </div>
          </div>

          <div>
            <Label className="text-[#F4F1EA]">Rental Type</Label>
            <div className="flex gap-4 mt-2 text-[#F4F1EA]">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="rental_type"
                  value="daily"
                  checked={formData.rental_type === 'daily'}
                  onChange={onInputChange}
                  className="mr-2 accent-[#B8860B]"
                />
                <span>Daily</span>
              </label>
              {equipment.weekly_rate > 0 && (
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="rental_type"
                    value="weekly"
                    checked={formData.rental_type === 'weekly'}
                    onChange={onInputChange}
                    className="mr-2 accent-[#B8860B]"
                  />
                  <span>Weekly (7 days)</span>
                </label>
              )}
            </div>
          </div>

          {showAttachments && (
            <div>
              <Label htmlFor="selected_attachment" className="text-[#F4F1EA]">
                Select Attachment (Included)
              </Label>
              <select
                data-testid="tractor-attachment-select"
                id="selected_attachment"
                name="selected_attachment"
                value={formData.selected_attachment}
                onChange={onInputChange}
                required
                className="w-full mt-1 p-2 bg-[#050814] border border-[#B8860B]/30 text-[#F4F1EA] rounded"
              >
                <option value="">Choose an attachment</option>
                {attachments.map((att) => (
                  <option key={att.id} value={att.id}>
                    {att.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name" className="text-[#F4F1EA]">First Name</Label>
              <Input
                data-testid="rental-first-name"
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={onInputChange}
                required
                className={inputClasses}
              />
            </div>
            <div>
              <Label htmlFor="last_name" className="text-[#F4F1EA]">Last Name</Label>
              <Input
                data-testid="rental-last-name"
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={onInputChange}
                required
                className={inputClasses}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email" className="text-[#F4F1EA]">Email</Label>
            <Input
              data-testid="rental-email"
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={onInputChange}
              required
              className={inputClasses}
            />
          </div>

          <div>
            <Label htmlFor="phone" className="text-[#F4F1EA]">Phone Number</Label>
            <Input
              data-testid="rental-phone"
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={onInputChange}
              required
              className={inputClasses}
            />
          </div>

          <PricingSummary pricing={pricing} />

          <SmsConsent
            testid="rental-sms-consent"
            checked={smsConsent}
            onChange={onSmsConsentChange}
          />

          <Button
            data-testid="rental-checkout-btn"
            type="submit"
            disabled={bookingLoading}
            className="w-full bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA] font-bold py-6 text-lg"
          >
            {bookingLoading ? 'Processing...' : 'Proceed to Checkout'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
